import { HistoricalDataAlert } from './GeneralInput.styles';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import {
  ControlBar,
  IControlButton,
  InfoBlock,
  MainContainer,
  ConfirmationModal,
  FormItemLabel,
  FormGrid,
  Loader,
  IFieldValues,
  useNotification,
} from '../../components';
import { generatePath, useNavigate } from 'react-router';
import URLs from '../URLs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSalonStore } from '../../store';
import InfoIcon from '@mui/icons-material/Info';
import Button from '@mui/material/Button';
import DateRangesBlock, { Quarters } from './DateRangesBlock';
import HistoricalDataModal from './HistoricalData/HistoricalDataModal';
import {
  ISalonInputFormItem,
  SalonInfoMap,
  SalonInputFormMap,
  SalonInputTitleDescription,
} from '../../types';
import { useTranslation } from 'react-i18next';
import { filterAndRenameObjectKeys } from '../../utils';

const mapCYToFYPeriod = (cYear: string, cQuarter: number): Record<string, string> => {
  let fYear = parseInt(cYear);
  let fQuarter = cQuarter + 2;

  if (fQuarter > 3) {
    fQuarter -= 4;
    fYear += 1;
  } else if (fQuarter < 0) {
    fQuarter += 4;
    fYear -= 1;
  }

  return {
    'fiscal Year': `FY${fYear.toString()}`,
    'fiscal Quarter': Quarters[fQuarter]?.split(':')?.[0],
  };
};
const GeneralInput = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [totalValues, setTotalValues] = useState({ tnTransactions: 0, totalSalonSales: 0 });
  const { salonId, planId, year, quarter } = useParams();
  const {
    isLoading,
    salon,
    fetchSalonInputData,
    salonInputData,
    updateSalonInputData,
    fetchSalon,
  } = useSalonStore();
  const [isHistoricalModalOpen, setIsHistoricalModalOpen] = useState(false);

  const [calendarPeriod, setCalendarPeriod] = useState<[string, number]>([
    year || new Date().getFullYear().toString(),
    Number(quarter) - 1 || 0,
  ]);
  const changePeriod = (cYear: string, cQuarter: number): void => {
    setCalendarPeriod([cYear, cQuarter]);
  };

  const { showMessage } = useNotification();

  const [currentValues, setCurrentValues] = useState<IFieldValues>(null);

  useEffect(() => {
    if (salonId && !salon) {
      fetchSalon(salonId, showMessage, navigate);
    }
  }, [salon, salonId]);

  useEffect(() => {
    if (salonId) {
      fetchSalonInputData(salonId, calendarPeriod[0], calendarPeriod[1] + 1, showMessage, navigate);
    }
  }, [salonId, calendarPeriod]);

  useEffect(() => {
    setTotalValues({
      tnTransactions:
        Number(currentValues?.tnRetailOnlyTr) + Number(currentValues?.tnServiceTransactions),
      totalSalonSales:
        Number(currentValues?.totalRetailSales) + Number(currentValues?.totalServiceSales),
    });
  }, [currentValues]);

  const requiredFields = useMemo(
    () =>
      [...Object.values(SalonInputFormMap)]
        .flat()
        .filter((item: ISalonInputFormItem) => item.required)
        .map((item: ISalonInputFormItem) => item.id),
    [],
  );

  const isInvalid = useCallback(
    (): boolean => !currentValues || requiredFields.some((id) => !String(currentValues[id])),
    [currentValues],
  );
  const calculate = () => {
    updateSalonInputData(salonId, calendarPeriod, currentValues, showMessage, navigate, (data) => {
      navigate(
        generatePath(`${URLs.Salon}/${URLs.KpiResults}`, {
          salonId,
          inputId: data.id,
          year: data.year,
          quarter: data.quarter,
          planId: planId || null,
        }),
      );
    });
  };

  const handleModalOpen = () => setIsHistoricalModalOpen(true);
  const handleModalClose = () => setIsHistoricalModalOpen(false);

  const rightButtons: IControlButton[] = [
    { label: t('cancel'), action: () => setIsModalOpen(true) },
    {
      label: t('calculate'),
      action: calculate,
      disabled: isInvalid(),
      variant: 'contained',
    },
  ];

  const totalFields = [
    {
      label: 'total # of transactions',
      id: 'tnTransactions',
      InputProps: {
        readOnly: true,
      },
    },
    {
      label: 'total salon sales',
      id: 'totalSalonSales',
      InputProps: {
        readOnly: true,
      },
    },
  ];

  return (
    <>
      <ControlBar
        backButtonAction={() => setIsModalOpen(true)}
        title={<Typography variant="h5">{salonInputData?.title || t('newPlan')}</Typography>}
        withKpiButton
      />
      <Loader isLoading={isLoading} />
      <MainContainer>
        <Typography variant="h6" textTransform={'uppercase'}>
          {salon?.title}
        </Typography>
        <InfoBlock
          infoList={filterAndRenameObjectKeys(salon, SalonInfoMap)}
          additionalInfo={mapCYToFYPeriod(calendarPeriod[0], calendarPeriod[1])}
        />
        <HistoricalDataAlert
          icon={
            <Tooltip
              title={
                "Please input data for the year preceding the last quarter. For example, if the quarter you're assessing is Q4 2024, then input historical data for 2023."
              }
              arrow
              placement={'top'}
            >
              <InfoIcon />
            </Tooltip>
          }
          action={
            <Button onClick={handleModalOpen} variant={'outlined'}>
              {t('inputHistoricalData')}
            </Button>
          }
        >
          {t('historicalData')}
        </HistoricalDataAlert>
        <Box mt={6}>
          <Typography variant="h6">
            <FormItemLabel
              label={'Date Range'}
              description={
                'Please specify the year and quarter for which you are conducting the Quarterly Business Review (QBR).'
              }
            />
          </Typography>
          <Grid container mt={1} spacing={2} data-testid="date-range-form">
            <DateRangesBlock
              year={calendarPeriod[0]}
              quarter={calendarPeriod[1]}
              onChange={changePeriod}
            />
          </Grid>
        </Box>
        {Object.entries(SalonInputFormMap).map((item: Record<string, any>) => (
          <FormGrid
            key={item[0]}
            formName={item[0]}
            formDescription={
              SalonInputTitleDescription[item[0] as keyof typeof SalonInputTitleDescription]
            }
            fields={item[1]}
            initialValues={salonInputData}
            onChangeFieldValues={(values) =>
              setCurrentValues((prevState) => ({ ...prevState, ...values }))
            }
          />
        ))}
        <FormGrid
          formName={t('total')}
          formDescription={
            "These fields are non-editable summaries of information from the 'Total # Service Transactions' and 'Total Service Sales' fields, provided for convenience and clarity."
          }
          fields={totalFields}
          initialValues={totalValues}
        />
        <HistoricalDataModal open={isHistoricalModalOpen} handleClose={handleModalClose} />
      </MainContainer>
      <ControlBar rightButtons={rightButtons} />
      <ConfirmationModal open={isModalOpen} handleClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default GeneralInput;
